import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import * as models from '../../../models';
import * as services from '../../../services'
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { InviteToOtherCompanyDialogComponent, RestrictionNotificationComponent, UpdateRoleComponent, UserAddComponent, UserEditComponent } from 'app/dialogs';
import { AppStorage } from '../../../services';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
    standalone: false
})
export class UserListComponent implements OnInit, AfterViewInit, OnDestroy {
  companySub: Subscription;
  role: models.Role;
  tableLoading: boolean = true;

  disableUsersClick: boolean = false;
  enableUsersClick: boolean = false;
  deleteUsersClick: boolean = false;
  sendResetPasswordLinkClick: boolean = false;

  companies: models.Company[] = [];

  get canSeeInviteToOtherCompanies() {
    return this.companies.length > 0;
  }

  get canAddUsers() {
    return this.role?.permissions?.includes(models.Permissions.addUsers);
  }

  get canEditUsers() {
    return this.role?.permissions?.includes(models.Permissions.editUsers);
  }

  get canDeleteUsers() {
    return this.role?.permissions?.includes(models.Permissions.deleteUsers);
  }

  get canEnableDisableUsers() {
    return this.role?.permissions?.includes(models.Permissions.enableDisableUsers);
  }

  get canEditUserRoles() {
    return this.role?.permissions?.includes(models.Permissions.editUserRoles);
  }

  users: Array<models.User> = [];
  invitedUsers: Array<models.User> = [];

  selectedRows: Array<UserListItem> = [];
  listUsers: UserListItem[] = null;

  constructor(
    private userService: services.UserService,
    private authService: services.AuthService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar
    , private helperService: services.HelperService
    , private appStorage: AppStorage
    , private notifierService: services.NotifierService
    , private companyService: services.CompanyService
    , private claimsService: services.ClaimsService
    , private roleService: services.RoleService
  ) { }

  async ngOnInit() {
    this.role = await this.claimsService.getRoleAwait();
    this.getCompanies();
    if (this.helperService.currentCompanyId != null) {
      this.getFromServer();
    }
  }

  ngAfterViewInit(): void {
    //set up subscription to listen to company id changes
    this.companySub = this.helperService.getCurrentCompanyId().subscribe(data => {
      this.getFromServer();
    })
  }

  ngOnDestroy() {
    if (this.companySub != null) {
      this.companySub.unsubscribe();
    }
  }

  async getCompanies() {
    let companyId = this.helperService.currentCompanyId;
    let companyResponse = await this.companyService.getAllCompaniesForUser();
    if (companyResponse.success) {
      this.companies = companyResponse.value.filter(i => i.uid != companyId);
    }
  }

  objectsAreSame(x, y): boolean {
    let objectsAreSame: boolean = true;
    for (var propertyName in x) {
      if (x[propertyName] !== y[propertyName]) {
        objectsAreSame = false;
        break;
      }
    }
    return objectsAreSame;
  }

  private actionRows(item?: UserListItem): UserListItem[] {
    return item ? [item] : this.selectedRows;
  }

  editUser(item?: UserListItem) {
    let firstSelectedRow: UserListItem = this.actionRows(item)[0];
    if (!firstSelectedRow) { return; }
    if (firstSelectedRow.invited) {
      this.notifierService.error('Cannot edit an invited user.  User must accept invitation.');
      return;
    }

    let dialogRef = this.dialog.open(UserEditComponent, {
      data: firstSelectedRow.user
    });

    dialogRef.afterClosed().subscribe(async result => {
      // if(result && result.submitted){
      //   this.event.assignedUserIds = result.data;
      // }
      this.getFromServer();
    });
  }

  updateRole(item: UserListItem) {
    if (!item || item.invited) { return; }
    const dialogRef = this.dialog.open(UpdateRoleComponent, {
      panelClass: 'med-width-dialog',
      data: { email: item.user.emailAddress, roleId: item.user.roleId }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) { this.getFromServer(); }
    });
  }

  disableSelectedRows(item?: UserListItem) {
    let userListItems: UserListItem[] = this.actionRows(item);
    let users = userListItems.filter(i => !i.invited).map(i => i.user);
    if (users.length == 0) {
      this.notifierService.error('Users selected are invited.  Cannot disable invited users.');
      return;
    }

    this.disableUsersClick = true;

    this.authService.disableUsers(users).then(response => {
      if (response.data.success) {
        this.notifierService.success(response.data.message)
        this.getFromServer();
      } else {
        this.snackBar.open("Error! " + response.data.message, null, {
          duration: 3000,
          verticalPosition: 'top'
        });
      }

      this.disableUsersClick = false;
    }).catch(error => {
      this.snackBar.open("Error!", null, {
        duration: 3000,
        verticalPosition: 'top'
      });

      this.disableUsersClick = false;
    })
  }

  enableSelectedRows(item?: UserListItem) {
    let userListItems: UserListItem[] = this.actionRows(item);
    let users = userListItems.filter(i => !i.invited).map(i => i.user);
    if (users.length == 0) {
      this.notifierService.error('Users selected are invited.  Cannot enable invited users.');
      return;
    }

    this.enableUsersClick = true;

    this.authService.enableUsers(users).then(response => {
      if (response.data.success) {
        this.notifierService.success(response.data.message)
        this.getFromServer();
      } else {
        this.snackBar.open("Error! " + response.data.message, null, {
          duration: 3000,
          verticalPosition: 'top'
        });
      }

      this.enableUsersClick = false;
    }).catch(error => {
      this.snackBar.open("Error!", null, {
        duration: 3000,
        verticalPosition: 'top'
      });

      this.enableUsersClick = false;
    })
  }

  deleteSelectedRowsClick() {
    this.notifierService.confirm('Delete Users', 'Are you sure you want to delete the selected Users?',
      () => { this.deleteSelectedRows() },
      () => { })
  }

  deleteSelectedRows() {
    let userListItems: Array<UserListItem> = this.selectedRows;
    let users = userListItems.filter(i => !i.invited).map(i => i.user);
    let userIds = users.map(i => i.id);

    if (users.length == 0) {
      this.notifierService.error('Users selected are invited.  Cannot delete invited users.');
      return;
    }

    this.deleteUsersClick = true;
    let companyId = this.helperService.currentCompanyId;

    let request = {
      userIds: userIds,
      companyId: companyId
    }

    this.authService.deleteUsers(request).then(response => {
      if (response.data.success) {
        this.notifierService.success(response.data.message)
        this.getFromServer();
      } else {
        this.snackBar.open("Error! " + response.data.message, null, {
          duration: 3000,
          verticalPosition: 'top'
        });
      }

      this.deleteUsersClick = false;
    }).catch(error => {
      this.snackBar.open("Error!", null, {
        duration: 3000,
        verticalPosition: 'top'
      });

      this.deleteUsersClick = false;
    })
  }

  inviteToOtherCompany(item?: UserListItem) {
    let firstSelectedRow: UserListItem = this.actionRows(item)[0];
    if (!firstSelectedRow) { return; }
    if (firstSelectedRow.invited) {
      this.notifierService.error('Cannot edit an invited user.  User must accept invitation.');
      return;
    }

    let dialogRef = this.dialog.open(InviteToOtherCompanyDialogComponent, {
      data: {
        user: firstSelectedRow.user,
        companyId: this.helperService.currentCompanyId,
        companies: this.companies
      }
    });

    // dialogRef.afterClosed().subscribe(async result => {
    //   this.getFromServer();
    // });
  }

  sendResetPasswordLink(item?: UserListItem) {
    this.sendResetPasswordLinkClick = true;

    let userListItems: Array<UserListItem> = this.actionRows(item);
    let users = userListItems.map(i => i.user);

    users.forEach(user => {
      this.authService.sendPasswordResetLink(user.emailAddress).then(data => {
        this.notifierService.success('Emails have been sent to users selected')
        console.log('Email Sent');

        this.sendResetPasswordLinkClick = false;
      }).catch(error => {
        this.snackBar.open(error, null, {
          duration: 3000,
          verticalPosition: 'top'
        });

        this.sendResetPasswordLinkClick = false;
      })
    });
  }

  async getFromServer() {
    this.tableLoading = true;
    this.selectedRows = [];
    this.listUsers = null;

    //find the company and go get the users for that company
    let companyId = this.helperService.currentCompanyId;

    let call1 = this.userService.getUsersFoCompany(companyId);
    let call2 = this.userService.getInvitedUsers(companyId);

    Promise.all([call1, call2]).then((values) => {
      let userResponse = values[0];
      let invitedUsers = values[1];
      let listUsers: UserListItem[] = [];

      if (!userResponse.data.success) {
        this.notifierService.error(userResponse.data.message);
      } else {
        //put all users returned from the server into the list
        let users: models.User[] = []
        userResponse.data.value.forEach(element => {
          let user: models.User = element;
          user.id = element.id;
          users.push(user);
        });

        this.users = users;
        users.forEach(user => {
          let listUser: UserListItem = {
            user: user,
            invited: false
          }
          listUsers.push(listUser);
        });
      }

      this.invitedUsers = invitedUsers;
      invitedUsers.forEach(user => {
        if (listUsers.find(i => i.user.id == user.id) != null) {
          return;
        }
        let listUser: UserListItem = {
          user: user,
          invited: true
        }
        listUsers.push(listUser);
      });

      this.enrichRows(listUsers).then(rows => {
        this.listUsers = rows;
        this.tableLoading = false;
      });
    }).catch(() => {
      this.tableLoading = false;
      this.notifierService.error('Unable to load users.');
    });
  }

  private async enrichRows(rows: UserListItem[]): Promise<UserListItem[]> {
    const roles = await this.roleService.getRoles().catch(() => [] as models.Role[]);
    const roleNames = new Map((roles || []).map(role => [role.id, role.name]));
    await Promise.all(rows.map(async item => {
      item.roleName = roleNames.get(item.user.roleId) || item.user.role?.name || '';
      item.statusLabel = item.invited ? 'Invited' : (item.user.disabled ? 'Disabled' : 'Active');
      item.statusClass = item.invited ? 'pc-status--invited' : (item.user.disabled ? 'pc-status--disabled' : 'pc-status--active');
      if (item.user.id) {
        item.imageUrl = await this.userService.getUserImageUrl(item.user.id).catch(() => null);
      }
    }));
    return rows;
  }

  userInitials(user: models.User): string {
    const source = user.displayName || user.emailAddress || '?';
    const parts = source.trim().split(/\s+/).filter(Boolean);
    return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : source.slice(0, 2)).toUpperCase();
  }

  addUser() {
    if (this.helperService.company_freeVersion && this.users.length >= this.appStorage.freeUsers) {
      let restrictionDialog = this.dialog.open(RestrictionNotificationComponent, {
        width: '50%',
        data: 'userAdd'
      });

      return;
    }

    let dialogRef = this.dialog.open(UserAddComponent, {
      panelClass: 'med-width-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      //reload list
      this.getFromServer();
      console.log(JSON.stringify(result));
    })
  }

  export() {
    const esc = (value: any) => '"' + String(value ?? '').replace(/"/g, '""') + '"';
    const lines = [['Display Name', 'Email Address', 'Role', 'Status'].map(esc).join(',')];
    (this.listUsers || []).forEach(item => lines.push([
      item.user.displayName, item.user.emailAddress, item.roleName, item.statusLabel
    ].map(esc).join(',')));
    const url = URL.createObjectURL(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'UserList_Export.csv';
    link.click();
    URL.revokeObjectURL(url);
  }
}

export interface UserListItem {
  user: models.User;
  invited: boolean;
  roleName?: string;
  imageUrl?: string;
  statusLabel?: string;
  statusClass?: string;
}
